import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { BidderApi, ITrafficEntityError, ITrafficEntityFields } from '../../../../modules/apis/bidder.api';
import {
	emptyTrafficFields,
	missingTrafficFields,
	splitTrafficErrors,
	TrafficForm,
} from '../../../../modules/components/forms/bidder-traffic/bidder-traffic.form';
import { IAdminxStore } from '../../../../modules/stores';
import routes from '../../../../modules/routes/index.routes';

export const CreateTraffic = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();
	const { dispatchLog } = useLogs();
	const navigate = useNavigateParams();
	const [fields, setFields] = useState<ITrafficEntityFields>(emptyTrafficFields());
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITrafficEntityFields, string>>>({});
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		bidderStore.getInputOptions().fetch();
	}, []);

	const onCreate = () => {
		const missing = missingTrafficFields(fields);
		if (Object.keys(missing).length) {
			setFieldErrors(missing);
			return;
		}

		setIsSaving(true);
		BidderApi.createTrafficEntity(fields)
			.then(() => {
				navigate(routes.bidder.traffic());
				dispatchLog({
					msg: 'Traffic entity was successfully created',
					type: DispatchLogsTypes.Success,
				});
			})
			.catch((error: { data?: { errors?: ITrafficEntityError[] } }) => {
				const { fieldErrors: nextFieldErrors, messages } = splitTrafficErrors(error.data?.errors);
				setFieldErrors(nextFieldErrors);
				if (messages.length) {
					messages.forEach(msg => dispatchLog({ msg, type: DispatchLogsTypes.Error }));
				} else if (!error.data?.errors?.length) {
					dispatchLog({ msg: 'Failed to create traffic entity', type: DispatchLogsTypes.Error });
				}
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<Disable is={isSaving}>
			<Page>
				<Pagebar>
					<PagebarTitle>Create Traffic</PagebarTitle>
				</Pagebar>
				<TrafficForm
					fields={fields}
					onChange={setFields}
					fieldErrors={fieldErrors}
					onClearError={field => setFieldErrors(current => ({ ...current, [field]: undefined }))}
				/>
			</Page>
			<BottomBar>
				<BottomBarActions>
					<PrimaryButton
						onClick={onCreate}
						isRotateIcon={isSaving}
						icon={isSaving ? 'loading-01' : 'send-03'}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'create_traffic_button')] }}>
						Create
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</Disable>
	);
});
